/**
 * SolutionPage — Full-page deep dive into a single workshop solution.
 * Route: /solutions/:id
 *
 * Reuses shared report components (reportStyles, ReportNav, SolutionAnalysis).
 * Shows workshop context, full description, SWOT, evaluation, implementation plan.
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Printer, ArrowLeft, Target, Users, Lightbulb, Zap, FileText, BarChart3, Merge } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from '../hooks/useTranslation.jsx';
import SolutionAnalysis, {
  getCompositeScore,
  SolutionConclusion,
  isTagBasedEvaluation,
} from '../components/Report/SolutionAnalysis';
import EvaluationTagChips from '../components/Solutions/EvaluationTagChips';
import SolutionImplementationPlan from '../components/Report/SolutionImplementationPlan';
import ReportNav from '../components/Report/ReportNav';
import {
  Container, Main, Section, SectionTitle, SectionSubtitle, Paragraph, Divider,
  LoadingContainer, Spinner, LoadingText, ErrorContainer,
} from '../components/Report/reportStyles';

// ─── Solution-specific styles (only what reportStyles doesn't have) ──────────

const PageWrap = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  @media print { background: #fff; }
`;

const Header = styled.header`
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
  color: #fff;
  padding: 60px 32px 48px;
  @media print {
    background: #1a1a2e !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    padding: 24px 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  @media print { margin-bottom: 12px; }
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  &:hover { color: #fff; }
  @media print { display: none; }
`;

const PrintButton = styled.button`
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: rgba(255,255,255,0.25); }
  @media print { display: none; }
`;

const WorkshopLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.5);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const FocusQuestion = styled.div`
  font-size: 15px;
  color: rgba(255,255,255,0.7);
  line-height: 1.4;
  font-style: italic;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: clamp(22px, 4vw, 34px);
  font-weight: 800;
  margin: 0;
  line-height: 1.25;
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const ScoreBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 22px;
  font-weight: 800;
  span { font-size: 14px; font-weight: 500; opacity: 0.7; }
`;

const EvalPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const EvalPill = styled.div`
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.9);
`;

const BodyText = styled.div`
  font-size: 16px;
  line-height: 1.85;
  color: #333;
  white-space: pre-line;
`;

const SubSection = styled.div`
  margin-top: 20px;
`;

const SubLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
`;

const HumanRatioTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  background: ${p => p.$ratio >= 0.5 ? '#f0fdf4' : '#f8fafc'};
  color: ${p => p.$ratio >= 0.5 ? '#16a34a' : '#6b7280'};
  border: 1px solid ${p => p.$ratio >= 0.5 ? '#bbf7d0' : '#e5e7eb'};
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-top: 10px;
`;

const ImpactCard = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #f3f4f6;
`;

const ImpactLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$color || '#6b7280'};
  margin-bottom: 6px;
`;

const ImpactText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin: 0;
`;

const HybridBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 10px;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 12px;
`;

const SourceTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const SourceTag = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,0.85);
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  text-decoration: none;
  &:hover { background: rgba(255,255,255,0.2); color: #fff; }
  @media print { color: #c4b5fd; background: rgba(139, 92, 246, 0.1); border-color: #c4b5fd; }
`;

const PlusSign = styled.span`
  font-size: 16px;
  font-weight: 300;
  color: rgba(255,255,255,0.4);
`;

const PageFooter = styled.footer`
  text-align: center;
  padding: 32px 0;
  margin-top: 48px;
  font-size: 13px;
  color: #9ca3af;
  border-top: 1px solid #e5e7eb;
  @media print { margin-top: 16px; }
`;

const SourceSolutionsBlock = styled.div`
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const SourceSolutionsLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const SourceSolutionItem = styled.div`
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-left: 3px solid #8b5cf6;
  border-radius: 0 8px 8px 0;
  margin-bottom: 8px;
  overflow: hidden;
`;

const SourceSolutionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;

const SourceSolutionTitle = styled(Link)`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  text-decoration: none;
  &:hover { color: #3b82f6; }
`;

const ExpandIcon = styled.span`
  font-size: 18px;
  font-weight: 300;
  color: #9ca3af;
  width: 24px;
  text-align: center;
  flex-shrink: 0;
`;

const SourceSolutionBody = styled.div`
  padding: 0 16px 14px;
  font-size: 14px;
  line-height: 1.7;
  color: #555;
  white-space: pre-line;
  border-top: 1px solid #f3f4f6;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function SolutionPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [solution, setSolution] = useState(null);
  const [workshopTitle, setWorkshopTitle] = useState('');
  const [showImplementationPlans, setShowImplementationPlans] = useState(true);
  const [showExperimental, setShowExperimental] = useState(false);
  const [focusQuestion, setFocusQuestion] = useState('');
  const [sourceSolutions, setSourceSolutions] = useState([]);
  const [expandedSource, setExpandedSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(API_ENDPOINTS.SOLUTION(id), { credentials: 'include' });
        if (!res.ok) throw new Error(`Solution not found (${res.status})`);
        const data = await res.json();
        setSolution(data);

        if (data.instance_id) {
          fetch(API_ENDPOINTS.INSTANCE(data.instance_id))
            .then(r => r.ok ? r.json() : null)
            .then(inst => {
              if (inst?.title) setWorkshopTitle(inst.title);
              if (inst?.settings?.showImplementationPlans === false) {
                setShowImplementationPlans(false);
              }
            })
            .catch(() => {});
          fetch(API_ENDPOINTS.PERSPECTIVES(data.instance_id))
            .then(r => r.ok ? r.json() : null)
            .then(persp => {
              const selected = (Array.isArray(persp) ? persp : []).find(p => p.is_selected);
              if (selected?.title) setFocusQuestion(selected.title);
            })
            .catch(() => {});
        }

        // Fetch source solution titles for hybrids
        if (data.is_hybrid && data.source_solution_ids?.length) {
          Promise.all(data.source_solution_ids.map(sid =>
            fetch(API_ENDPOINTS.SOLUTION(sid), { credentials: 'include' }).then(r => r.ok ? r.json() : null).catch(() => null)
          )).then(sources => {
            setSourceSolutions(sources.filter(Boolean).map(s => ({
              id: s.id,
              title: s.metadata?.ai_title || s.title?.split(/[.!?\n]/)[0] || s.title,
              humanText: s.metadata?.humanText || null,
              fullText: s.title || s.description,
            })));
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  if (loading) return <LoadingContainer><Spinner /><LoadingText>{t('solutionPage.loading')}</LoadingText></LoadingContainer>;
  if (error || !solution) return <ErrorContainer><h2>{t('solutionPage.notFound')}</h2><p>{error || t('solutionPage.unknownError')}</p></ErrorContainer>;

  const sol = solution;
  const compositeScore = getCompositeScore(sol.ai_analysis);
  const evaluation = sol.ai_analysis?.evaluation;
  const tagEval = isTagBasedEvaluation(evaluation);
  const title = sol.metadata?.ai_title || sol.title?.split(/[.!?\n]/)[0] || sol.title;
  // Human content ratio
  const humanRatio = (() => {
    if (sol.metadata?.humanRatio != null) return parseFloat(sol.metadata.humanRatio);
    const segs = sol.metadata?.textSegments;
    if (!segs?.length) return null;
    const totalLen = segs.reduce((sum, s) => sum + (s.text?.length || 0), 0);
    if (!totalLen) return null;
    const humanLen = segs.filter(s => s.source === 'human').reduce((sum, s) => sum + (s.text?.length || 0), 0);
    return humanLen / totalLen;
  })();

  const hasImpact = sol.impact_mindsets || sol.impact_structures || sol.impact_behavior;
  const hasContext = sol.problem || sol.resources || sol.why_future;
  const hasAnalysis = sol.ai_analysis?.swot;
  const hasPlan =
    showImplementationPlans &&
    (sol.ai_analysis?.implementationPlan?.timeline?.length > 0 ||
      sol.ai_analysis?.implementationPlan?.phases?.length > 0);
  const reportUrl = sol.instance_id ? `/raportti?kid=${sol.instance_id}` : '/raportti';

  const hasSummary = sol.metadata?.humanText || sol.hybrid_rationale;
  const navSections = [
    ...(hasSummary ? [{ id: 'summary', label: t('solutionPage.navTabs.summary') }] : []),
    { id: 'description', label: t('solutionPage.navTabs.description') },
    ...(hasContext ? [{ id: 'context', label: t('solutionPage.navTabs.context') }] : []),
    ...(hasImpact ? [{ id: 'impact', label: t('solutionPage.navTabs.impact') }] : []),
    ...(hasAnalysis ? [{ id: 'analysis', label: t('solutionPage.navTabs.analysis') }] : []),
    ...(hasPlan ? [{ id: 'plan', label: t('solutionPage.navTabs.plan') }] : []),
  ];

  return (
    <PageWrap>
      <ReportNav sections={navSections} />

      <Header id="header">
        <HeaderContent>
          <HeaderTop>
            <BackLink to={reportUrl}><ArrowLeft size={16} /> {t('solutionPage.backToReport')}</BackLink>
            <PrintButton onClick={() => window.print()}><Printer size={18} /> {t('solutionPage.printPdf')}</PrintButton>
          </HeaderTop>

          <WorkshopLabel>
            {workshopTitle
              ? t('solutionPage.workshopLabelWithTitle', { title: workshopTitle })
              : t('solutionPage.workshopLabel')}
          </WorkshopLabel>
          {focusQuestion && <FocusQuestion>{focusQuestion}</FocusQuestion>}

          {sol.is_hybrid && (
            <HybridBadge><Merge size={14} /> {t('solutionPage.hybridBadge')}</HybridBadge>
          )}

          <Title>{title}</Title>

          {sourceSolutions.length > 0 && (
            <SourceTags>
              {sourceSolutions.map((s, i) => (
                <span key={s.id} style={{ display: 'contents' }}>
                  {i > 0 && <PlusSign>+</PlusSign>}
                  <SourceTag to={`/solutions/${s.id}`}>{s.title}</SourceTag>
                </span>
              ))}
            </SourceTags>
          )}

          <HeaderMeta>
            {tagEval && (
              <div style={{ width: '100%', marginTop: 4 }}>
                <EvaluationTagChips
                  tags={evaluation.tags}
                  contradictsResearch={evaluation.contradicts_research}
                  researchStance={evaluation.research_stance}
                  variant="dark"
                />
              </div>
            )}
            {/* Composite score "X / 5" otsikkopalkista poistettu käsikirjoitus 2026-05 mukaan.
                Per-criteria pisteet näkyvät yhä alla EvalPills-listassa. */}
            {!tagEval && evaluation && (
              <EvalPills>
                {['impact', 'feasibility', 'innovation', 'evidence'].map((key) => {
                  const label = t(`solutionPage.scoreLabels.${key}`);
                  const data = evaluation[key];
                  if (!data?.score) return null;
                  return <EvalPill key={key}>{label} {data.score}/5</EvalPill>;
                })}
              </EvalPills>
            )}
          </HeaderMeta>
        </HeaderContent>
      </Header>

      <Main>
        {/* Summary: human-written original or hybrid rationale */}
        {(sol.metadata?.humanText || sol.hybrid_rationale) && (
          <Section id="summary">
            <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {t('solutionPage.navTabs.summary')}
              {humanRatio !== null && (
                <HumanRatioTag $ratio={humanRatio}>
                  {Math.round(humanRatio * 100)}% {t('solutionPage.humanWritten')}
                </HumanRatioTag>
              )}
            </SectionTitle>
            <BodyText>{sol.metadata?.humanText || sol.hybrid_rationale}</BodyText>
            <SolutionConclusion analysis={sol.ai_analysis} />
          </Section>
        )}

        {/* Full description (AI-enriched) */}
        <Section id="description">
          <SectionTitle style={!hasSummary && humanRatio !== null ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } : undefined}>
            {t('solutionPage.navTabs.description')}
            {!hasSummary && humanRatio !== null && (
              <HumanRatioTag $ratio={humanRatio}>
                {Math.round(humanRatio * 100)}% {t('solutionPage.humanWritten')}
              </HumanRatioTag>
            )}
          </SectionTitle>
          <BodyText>{sol.title || sol.description}</BodyText>
          {!hasSummary && <SolutionConclusion analysis={sol.ai_analysis} />}

          {/* Source solutions for hybrids */}
          {sol.is_hybrid && sourceSolutions.length > 0 && (
            <SourceSolutionsBlock>
              <SourceSolutionsLabel>{t('solutionPage.sourceSolutions')} ({sourceSolutions.length})</SourceSolutionsLabel>
              {sourceSolutions.map(src => (
                <SourceSolutionItem key={src.id}>
                  <SourceSolutionHeader onClick={() => setExpandedSource(prev => prev === src.id ? null : src.id)}>
                    <SourceSolutionTitle to={`/solutions/${src.id}`}>{src.title}</SourceSolutionTitle>
                    <ExpandIcon>{expandedSource === src.id ? '−' : '+'}</ExpandIcon>
                  </SourceSolutionHeader>
                  {expandedSource === src.id && (
                    <SourceSolutionBody>{src.humanText || src.fullText}</SourceSolutionBody>
                  )}
                </SourceSolutionItem>
              ))}
            </SourceSolutionsBlock>
          )}
        </Section>

        {hasContext && (
          <Section id="context">
            <SectionTitle>{t('solutionPage.navTabs.context')}</SectionTitle>
            {sol.problem && (
              <SubSection>
                <SubLabel><Target size={15} /> {t('solutionPage.problemLabel')}</SubLabel>
                <Paragraph>{sol.problem}</Paragraph>
              </SubSection>
            )}
            {sol.resources && (
              <SubSection>
                <SubLabel><Users size={15} /> {t('solutionPage.resourcesLabel')}</SubLabel>
                <Paragraph>{sol.resources}</Paragraph>
              </SubSection>
            )}
            {sol.why_future && (
              <SubSection>
                <SubLabel><Lightbulb size={15} /> {t('solutionPage.futureLabel')}</SubLabel>
                <Paragraph>{sol.why_future}</Paragraph>
              </SubSection>
            )}
          </Section>
        )}

        {hasImpact && (
          <Section id="impact">
            <SectionTitle>{t('solutionPage.navTabs.impact')}</SectionTitle>
            <ImpactGrid>
              {sol.impact_mindsets && (
                <ImpactCard>
                  <ImpactLabel $color="#8b5cf6">{t('solutionPage.impactLabels.mindsets')}</ImpactLabel>
                  <ImpactText>{sol.impact_mindsets}</ImpactText>
                </ImpactCard>
              )}
              {sol.impact_structures && (
                <ImpactCard>
                  <ImpactLabel $color="#3b82f6">{t('solutionPage.impactLabels.structures')}</ImpactLabel>
                  <ImpactText>{sol.impact_structures}</ImpactText>
                </ImpactCard>
              )}
              {sol.impact_behavior && (
                <ImpactCard>
                  <ImpactLabel $color="#10b981">{t('solutionPage.impactLabels.behavior')}</ImpactLabel>
                  <ImpactText>{sol.impact_behavior}</ImpactText>
                </ImpactCard>
              )}
            </ImpactGrid>
          </Section>
        )}

        {hasAnalysis && (
          <div id="analysis">
            <Divider />
            <SolutionAnalysis analysis={sol.ai_analysis} hideConclusion />
          </div>
        )}

        {hasPlan && (
          <div style={{ margin: '32px 0', display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => setShowExperimental(v => !v)}
              style={{
                padding: '10px 20px',
                background: showExperimental ? '#f3e8ff' : '#f9fafb',
                color: showExperimental ? '#6d28d9' : '#4b5563',
                border: `1px solid ${showExperimental ? '#e9d5ff' : '#d1d5db'}`,
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {showExperimental ? t('solutionPage.hideAiPlan') : t('solutionPage.showAiPlan')}
            </button>
          </div>
        )}

        {hasPlan && showExperimental && (
          <div id="plan">
            <SolutionImplementationPlan plan={sol.ai_analysis.implementationPlan} />
          </div>
        )}

        <PageFooter>
          {t('solutionPage.footer')}
          {sol.created_at && (
            <div style={{ marginTop: 4 }}>
              {t('solutionPage.createdDate', { date: new Date(sol.created_at).toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' }) })}
            </div>
          )}
        </PageFooter>
      </Main>
    </PageWrap>
  );
}
